import { Link } from '@tanstack/react-router';
import { Sparkles } from 'lucide-react';
import { useEffect, useState, type MouseEvent } from 'react';
import { Card, EmptyState, PageHeader } from '../components/ui.js';
import { formatLongDate } from '../lib/format.js';
import { useAppMeta, type AppMeta } from '../lib/meta.js';

/**
 * What's new: the release notes this deploy offers this person (core
 * platform/release-notes.ts, chosen in the app metadata), newest first, and the dot in the
 * menu while one is unread. Which notes someone has seen is remembered in their browser, per
 * organization, by date and title; with nothing remembered, a note is new for two weeks of the
 * organization's days.
 */

type Note = AppMeta['whats_new'][number];

const NEW_FOR_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;
const SEEN_EVENT = 'databink:whats-new-seen';

function noteKey(note: Note): string {
  return `${note.date} ${note.title}`;
}

function seenKey(userId: string, orgId: string): string {
  return `databink.whats-new.${userId}.${orgId}`;
}

/** The notes this person has seen here, or null when nothing is remembered. */
function readSeen(key: string): string[] | null {
  try {
    const stored = window.localStorage.getItem(key);
    if (stored === null) return null;
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : null;
  } catch {
    return null;
  }
}

/** The organization's today, YYYY-MM-DD, in its time zone. */
function orgToday(timeZone: string, now: Date): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** Whether any note is unread, given those seen, on the organization's day `today`. */
export function hasUnread(notes: Note[], seen: string[] | null, today: string): boolean {
  if (seen !== null) return notes.some((note) => !seen.includes(noteKey(note)));
  const day = Date.parse(`${today}T00:00:00Z`);
  return notes.some((note) => {
    const released = Date.parse(`${note.date}T00:00:00Z`);
    return Number.isFinite(released) && day - released < NEW_FOR_DAYS * DAY_MS;
  });
}

/** The notes for this person, and where their seen notes are kept. */
function useNotes() {
  const meta = useAppMeta();
  return {
    notes: meta.whats_new,
    key: seenKey(meta.user.id, meta.organization.id),
    timeZone: meta.organization.timezone,
  };
}

/** The menu's dot: a note this person hasn't seen since it arrived. */
export function useWhatsNewUnread(): boolean {
  const { notes, key, timeZone } = useNotes();
  const [seen, setSeen] = useState(() => readSeen(key));
  useEffect(() => {
    const update = () => {
      const now = readSeen(key);
      setSeen((was) => (JSON.stringify(was) === JSON.stringify(now) ? was : now));
    };
    const fromOtherTab = (event: StorageEvent) => {
      if (event.key === key || event.key === null) update();
    };
    update();
    window.addEventListener(SEEN_EVENT, update);
    window.addEventListener('storage', fromOtherTab);
    return () => {
      window.removeEventListener(SEEN_EVENT, update);
      window.removeEventListener('storage', fromOtherTab);
    };
  }, [key]);
  return hasUnread(notes, seen, orgToday(timeZone, new Date()));
}

/** The menu entry, with a dot while a note is unread. */
export function WhatsNewLink({
  className,
  active,
  onClick,
}: {
  className: string;
  active: string;
  onClick: (event: MouseEvent) => void;
}) {
  const unread = useWhatsNewUnread();
  return (
    <Link
      to="/whats-new"
      className={className}
      onClick={onClick}
      activeProps={{ className: active }}
      aria-label={unread ? 'What’s new, unread changes' : undefined}
    >
      <Sparkles className="size-4" aria-hidden /> What’s new
      {unread && <span className="ml-auto size-2 rounded-full bg-brand" aria-hidden />}
    </Link>
  );
}

export function WhatsNewScreen() {
  const meta = useAppMeta();
  const { notes, key } = useNotes();
  useEffect(() => {
    const seen = JSON.stringify(notes.map(noteKey));
    try {
      if (window.localStorage.getItem(key) === seen) return;
      window.localStorage.setItem(key, seen);
    } catch {
      // Not remembered: the dot shows until the notes are two weeks old.
    }
    window.dispatchEvent(new Event(SEEN_EVENT));
  }, [notes, key]);

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="What’s new"
        subtitle={`Changes to ${meta.branding.product_name}, newest first.`}
      />
      {notes.length === 0 ? (
        <EmptyState title="Nothing new yet">Changes show here as they arrive.</EmptyState>
      ) : (
        <Card padded={false}>
          <ol className="divide-y divide-slate-100">
            {notes.map((note) => (
              <li key={noteKey(note)} className="px-4 py-4 sm:px-5">
                <p className="text-xs font-medium text-slate-500">
                  <time dateTime={note.date}>{formatLongDate(note.date)}</time>
                </p>
                <h2 className="mt-1 text-base font-semibold text-slate-900">{note.title}</h2>
                <p className="mt-1 text-sm leading-6 text-slate-700">{note.body}</p>
                {note.path && (
                  <Link
                    to={note.path}
                    className="mt-2 inline-block text-sm font-medium text-brand-strong hover:underline"
                  >
                    Take a look
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </Card>
      )}
    </div>
  );
}
